import { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import { callUIAPI } from '../../../utils/apiUtils';
import * as apiConstants from '../../../utils/apiUtilsConstants';
import { toInputDateUTC, formatDateUTC, formatNoteDate, tomorrowInputDate, oneMonthFromNow } from './dateUtils';
import ConfirmDialog from './ConfirmDialog';

/**
 * Props:
 *   firmId             – firm ID
 *   firmName           – firm name (for dialog text)
 *   hasPaymentCustomer – whether a payment customer ID exists
 * Ref:
 *   exposes { refresh() } so parent can trigger a refetch after payment ID changes
 */
const FirmSubscription = forwardRef(({ firmId, firmName, hasPaymentCustomer }, ref) => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [trialEndDate, setTrialEndDate] = useState(oneMonthFromNow());

  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState(null);

  const [updatingDate, setUpdatingDate] = useState(false);
  const [updateDateError, setUpdateDateError] = useState(null);

  const [showEndDialog, setShowEndDialog] = useState(false);
  const [ending, setEnding] = useState(false);
  const [endError, setEndError] = useState(null);

  const [override, setOverride] = useState(null);
  const [overrideLoading, setOverrideLoading] = useState(false);
  const [overrideError, setOverrideError] = useState(null);
  const [overrideSaving, setOverrideSaving] = useState(false);
  const [overrideSaveError, setOverrideSaveError] = useState(null);

  const [overrideActive, setOverrideActive] = useState(false);
  const [overrideSourceContent, setOverrideSourceContent] = useState('');
  const [overrideContent, setOverrideContent] = useState('');
  const [overrideAiImages, setOverrideAiImages] = useState('');

  const [showDeleteOverrideDialog, setShowDeleteOverrideDialog] = useState(false);
  const [deletingOverride, setDeletingOverride] = useState(false);
  const [deleteOverrideError, setDeleteOverrideError] = useState(null);

  const fetchSubscription = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await callUIAPI(
        `bo/firms/${firmId}/subscription`,
        null,
        apiConstants.METHOD_GET,
        true,
      );
      const sub = response.data.data || null;
      setSubscription(sub);
      if (sub?.isExtendedTrial && sub?.trialEndsAt) {
        setTrialEndDate(toInputDateUTC(sub.trialEndsAt));
      } else if (!sub?.isExtendedTrial) {
        setTrialEndDate(oneMonthFromNow());
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load subscription.');
    } finally {
      setLoading(false);
    }
  }, [firmId]);

  const fetchOverride = useCallback(async () => {
    try {
      setOverrideLoading(true);
      setOverrideError(null);
      const response = await callUIAPI(
        `bo/firms/${firmId}/usage-override`,
        null,
        apiConstants.METHOD_GET,
        true,
      );
      const data = response.data.data || null;
      setOverride(data);
      if (data) {
        setOverrideActive(data.isActive);
        setOverrideSourceContent(String(data.sourceContent ?? 0));
        setOverrideContent(String(data.content ?? 0));
        setOverrideAiImages(String(data.aiImages ?? 0));
      } else {
        setOverrideActive(false);
        setOverrideSourceContent('');
        setOverrideContent('');
        setOverrideAiImages('');
      }
    } catch (err) {
      setOverrideError(err?.response?.data?.message || 'Failed to load usage override.');
    } finally {
      setOverrideLoading(false);
    }
  }, [firmId]);

  useEffect(() => {
    if (firmId) {
      fetchSubscription();
      fetchOverride();
    }
  }, [firmId, fetchSubscription, fetchOverride]);

  // Expose refresh to parent via ref
  useImperativeHandle(ref, () => ({
    refresh: () => {
      fetchSubscription();
      fetchOverride();
    },
  }), [fetchSubscription, fetchOverride]);

  const isOnFreeTier = subscription?.isFreeTier === true;
  const isOnExtendedTrial = subscription?.isExtendedTrial === true;
  const planName = subscription?.planName || null;
  const status = subscription?.status || null;

  const getStatusLabel = () => {
    if (!subscription) return 'No subscription';
    if (isOnExtendedTrial) return 'Extended Trial';
    if (status === 'trialing') return 'Trial';
    if (status === 'active') return 'Active';
    if (status === 'past_due') return 'Past Due';
    if (status === 'canceled') return 'Canceled';
    return status || 'Unknown';
  };

  const getStatusColor = () => {
    if (!subscription) return { bg: '#f3f4f6', text: '#6b7280' };
    if (isOnExtendedTrial) return { bg: '#e0e7ff', text: '#3730a3' };
    if (status === 'trialing') return { bg: '#dbeafe', text: '#1e40af' };
    if (status === 'active') return { bg: '#dcfce7', text: '#166534' };
    if (status === 'past_due') return { bg: '#fef9c3', text: '#854d0e' };
    return { bg: '#f3f4f6', text: '#6b7280' };
  };

  const statusLabel = getStatusLabel();
  const statusColor = getStatusColor();

  const currentTrialEndInput = subscription?.trialEndsAt ? toInputDateUTC(subscription.trialEndsAt) : '';
  const trialDateChanged = isOnExtendedTrial && trialEndDate !== currentTrialEndInput;

  const showTrialControls = !loading && hasPaymentCustomer && (isOnFreeTier || isOnExtendedTrial || !subscription);

  const parseIntOrZero = (val) => { const n = parseInt(val, 10); return isNaN(n) ? 0 : n; };

  const overrideFormDirty = (() => {
    if (!override) {
      // No existing record — dirty if any value is filled in
      return overrideSourceContent !== '' || overrideContent !== '' || overrideAiImages !== '';
    }
    return (
      overrideActive !== override.isActive ||
      parseIntOrZero(overrideSourceContent) !== (override.sourceContent ?? 0) ||
      parseIntOrZero(overrideContent) !== (override.content ?? 0) ||
      parseIntOrZero(overrideAiImages) !== (override.aiImages ?? 0)
    );
  })();

  const overrideFormValid =
    overrideSourceContent !== '' && parseIntOrZero(overrideSourceContent) >= 0 &&
    overrideContent !== '' && parseIntOrZero(overrideContent) >= 0 &&
    overrideAiImages !== '' && parseIntOrZero(overrideAiImages) >= 0;

  const openAssignDialog = () => { setAssignError(null); setShowAssignDialog(true); };
  const closeAssignDialog = () => { if (!assigning) { setShowAssignDialog(false); setAssignError(null); } };

  const handleAssign = async () => {
    try {
      setAssigning(true);
      setAssignError(null);
      await callUIAPI(`bo/firms/${firmId}/extended-trial`, { trialEndDate }, apiConstants.METHOD_PUT, true);
      setShowAssignDialog(false);
      fetchSubscription();
    } catch (err) {
      setAssignError(err?.response?.data?.message || 'Failed to assign extended trial.');
    } finally {
      setAssigning(false);
    }
  };

  const handleUpdateDate = async () => {
    try {
      setUpdatingDate(true);
      setUpdateDateError(null);
      await callUIAPI(`bo/firms/${firmId}/extended-trial`, { trialEndDate }, apiConstants.METHOD_PUT, true);
      fetchSubscription();
    } catch (err) {
      setUpdateDateError(err?.response?.data?.message || 'Failed to update trial end date.');
    } finally {
      setUpdatingDate(false);
    }
  };

  const openEndDialog = () => { setEndError(null); setShowEndDialog(true); };
  const closeEndDialog = () => { if (!ending) { setShowEndDialog(false); setEndError(null); } };

  const handleEnd = async () => {
    try {
      setEnding(true);
      setEndError(null);
      await callUIAPI(`bo/firms/${firmId}/extended-trial`, null, apiConstants.METHOD_DELETE, true);
      setShowEndDialog(false);
      fetchSubscription();
    } catch (err) {
      setEndError(err?.response?.data?.message || 'Failed to end extended trial.');
    } finally {
      setEnding(false);
    }
  };

  const handleSaveOverride = async () => {
    try {
      setOverrideSaving(true);
      setOverrideSaveError(null);
      const payload = {
        isActive: overrideActive,
        sourceContent: parseIntOrZero(overrideSourceContent),
        content: parseIntOrZero(overrideContent),
        aiImages: parseIntOrZero(overrideAiImages),
      };
      await callUIAPI(`bo/firms/${firmId}/usage-override`, payload, apiConstants.METHOD_PUT, true);
      fetchOverride();
    } catch (err) {
      setOverrideSaveError(err?.response?.data?.message || 'Failed to save usage override.');
    } finally {
      setOverrideSaving(false);
    }
  };

  const openDeleteOverrideDialog = () => { setDeleteOverrideError(null); setShowDeleteOverrideDialog(true); };
  const closeDeleteOverrideDialog = () => { if (!deletingOverride) { setShowDeleteOverrideDialog(false); setDeleteOverrideError(null); } };

  const handleDeleteOverride = async () => {
    try {
      setDeletingOverride(true);
      setDeleteOverrideError(null);
      await callUIAPI(`bo/firms/${firmId}/usage-override`, null, apiConstants.METHOD_DELETE, true);
      setShowDeleteOverrideDialog(false);
      setOverride(null);
      setOverrideActive(false);
      setOverrideSourceContent('');
      setOverrideContent('');
      setOverrideAiImages('');
    } catch (err) {
      setDeleteOverrideError(err?.response?.data?.message || 'Failed to remove usage override.');
    } finally {
      setDeletingOverride(false);
    }
  };

  return (
    <div className="firm-field-section">
      <div className="firm-field-row">
        <div className="firm-field-content">
          <h3 className="firm-field-label">Subscription</h3>

          {loading ? (
            <p className="firm-field-value" style={{ color: '#9ca3af' }}>Loading subscription…</p>
          ) : error ? (
            <p className="firm-field-error">{error}</p>
          ) : !hasPaymentCustomer ? (
            <p className="firm-field-value">
              <span className="firm-empty-value">No payment customer — create one first</span>
            </p>
          ) : !subscription ? (
            <p className="firm-field-value">
              <span className="firm-empty-value">No active subscription</span>
            </p>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <p className="firm-field-value" style={{ margin: 0 }}>
                {planName || 'Unknown Plan'}
              </p>
              <span
                className="firm-status-badge"
                style={{ backgroundColor: statusColor.bg, color: statusColor.text, marginTop: 0 }}
              >
                {statusLabel}
              </span>
              {isOnExtendedTrial && subscription.trialEndsAt && (
                <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                  Ends {formatDateUTC(subscription.trialEndsAt)}
                </span>
              )}
              {!isOnExtendedTrial && subscription.trialEndsAt && status === 'trialing' && (
                <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                  Trial ends {formatDateUTC(subscription.trialEndsAt)}
                </span>
              )}
            </div>
          )}

          {}
          {showTrialControls && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
              <label htmlFor="firm-trial-end-date" style={{ fontSize: '13px', color: '#374151', fontWeight: 500 }}>
                {isOnExtendedTrial ? 'Trial end date:' : 'Extended trial until:'}
              </label>
              <input
                id="firm-trial-end-date"
                type="date"
                className="firm-note-form-date"
                value={trialEndDate}
                onChange={(e) => { setTrialEndDate(e.target.value); setUpdateDateError(null); }}
                min={tomorrowInputDate()}
              />
              {(isOnFreeTier || !subscription) && (
                <button
                  className="firm-btn firm-btn-create"
                  onClick={openAssignDialog}
                  disabled={!trialEndDate}
                >
                  Assign Extended Trial
                </button>
              )}
              {isOnExtendedTrial && (
                <>
                  <button
                    className="firm-btn firm-btn-save"
                    onClick={handleUpdateDate}
                    disabled={updatingDate || !trialEndDate || !trialDateChanged}
                  >
                    {updatingDate ? 'Updating…' : 'Update Date'}
                  </button>
                  <button
                    className="firm-btn firm-btn-clear"
                    onClick={openEndDialog}
                    disabled={updatingDate}
                  >
                    End Extended Trial
                  </button>
                </>
              )}
              {updateDateError && (
                <p className="firm-field-error" style={{ width: '100%', marginTop: '4px' }}>{updateDateError}</p>
              )}
            </div>
          )}

          {}
          {!loading && hasPaymentCustomer && (
            <div style={{ marginTop: '16px', borderTop: '1px solid #e5e7eb', paddingTop: '14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <h4 style={{ margin: 0, fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>
                  Usage Limit Override
                </h4>
                {override && (
                  <span
                    className="firm-status-badge"
                    style={{
                      backgroundColor: override.isActive ? '#dcfce7' : '#f3f4f6',
                      color: override.isActive ? '#166534' : '#6b7280',
                      marginTop: 0,
                    }}
                  >
                    {override.isActive ? 'Active' : 'Inactive'}
                  </span>
                )}
              </div>

              {overrideLoading ? (
                <p style={{ fontSize: '0.8rem', color: '#9ca3af', margin: 0 }}>Loading override…</p>
              ) : overrideError ? (
                <p className="firm-field-error">{overrideError}</p>
              ) : (
                <>
                  <p style={{ fontSize: '0.8rem', color: '#6b7280', margin: '0 0 10px 0' }}>
                    Override values are per-user limits (multiplied by active user count), same as plan defaults.
                    When active, these replace the plan defaults regardless of subscription tier.
                  </p>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#374151', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={overrideActive}
                        onChange={(e) => setOverrideActive(e.target.checked)}
                        style={{ accentColor: '#4f46e5' }}
                      />
                      Enabled
                    </label>

                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: '#374151' }}>
                      Models
                      <input
                        type="number"
                        min="0"
                        className="firm-note-form-date"
                        style={{ width: '72px' }}
                        value={overrideSourceContent}
                        onChange={(e) => { setOverrideSourceContent(e.target.value); setOverrideSaveError(null); }}
                        placeholder="0"
                      />
                    </label>

                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: '#374151' }}>
                      Content
                      <input
                        type="number"
                        min="0"
                        className="firm-note-form-date"
                        style={{ width: '72px' }}
                        value={overrideContent}
                        onChange={(e) => { setOverrideContent(e.target.value); setOverrideSaveError(null); }}
                        placeholder="0"
                      />
                    </label>

                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: '#374151' }}>
                      AI Images
                      <input
                        type="number"
                        min="0"
                        className="firm-note-form-date"
                        style={{ width: '72px' }}
                        value={overrideAiImages}
                        onChange={(e) => { setOverrideAiImages(e.target.value); setOverrideSaveError(null); }}
                        placeholder="0"
                      />
                    </label>

                    <button
                      className="firm-btn firm-btn-save"
                      onClick={handleSaveOverride}
                      disabled={overrideSaving || !overrideFormValid || !overrideFormDirty}
                    >
                      {overrideSaving ? 'Saving…' : (override ? 'Update Override' : 'Save Override')}
                    </button>

                    {override && (
                      <button
                        className="firm-btn firm-btn-clear"
                        onClick={openDeleteOverrideDialog}
                        disabled={overrideSaving}
                      >
                        Remove Override
                      </button>
                    )}
                  </div>

                  {overrideSaveError && (
                    <p className="firm-field-error" style={{ marginTop: '4px' }}>{overrideSaveError}</p>
                  )}
                </>
              )}
            </div>
          )}
        </div>
        {!loading && hasPaymentCustomer && (
          <div className="firm-field-actions">
            <button className="firm-btn firm-btn-secondary" onClick={() => { fetchSubscription(); fetchOverride(); }}>Refresh</button>
          </div>
        )}
      </div>

      {}
      <ConfirmDialog
        open={showAssignDialog}
        onClose={closeAssignDialog}
        onConfirm={handleAssign}
        title="Assign Extended Trial?"
        icon="ℹ️"
        iconVariant="info"
        confirmLabel="Assign Extended Trial"
        confirmVariant="save"
        loading={assigning}
        loadingText="Assigning extended trial…"
        error={assignError}
      >
        <p className="firm-dialog-body">
          This will switch <strong>{firmName}</strong> from the free tier to the extended
          trial until <strong>{formatNoteDate(trialEndDate)}</strong>. They will have enhanced
          limits during this period and can subscribe to a paid plan at any time.
        </p>
        <p className="firm-dialog-body" style={{ marginTop: '8px', fontSize: '0.85rem', color: '#6b7280' }}>
          Any remaining free trial period will end. After the extended trial expires,
          the firm will need to subscribe to continue.
        </p>
      </ConfirmDialog>

      {}
      <ConfirmDialog
        open={showEndDialog}
        onClose={closeEndDialog}
        onConfirm={handleEnd}
        title="End Extended Trial?"
        icon="⚠"
        iconVariant="warning"
        confirmLabel="End Extended Trial"
        confirmVariant="danger"
        loading={ending}
        loadingText="Ending extended trial…"
        error={endError}
      >
        <p className="firm-dialog-body">
          This will immediately end the extended trial for <strong>{firmName}</strong>.
          The firm will no longer have an active subscription and will need to subscribe
          to a paid plan to continue using the application.
        </p>
      </ConfirmDialog>

      {}
      <ConfirmDialog
        open={showDeleteOverrideDialog}
        onClose={closeDeleteOverrideDialog}
        onConfirm={handleDeleteOverride}
        title="Remove Usage Override?"
        icon="⚠"
        iconVariant="warning"
        confirmLabel="Remove Override"
        confirmVariant="danger"
        loading={deletingOverride}
        loadingText="Removing override…"
        error={deleteOverrideError}
      >
        <p className="firm-dialog-body">
          This will permanently remove the usage limit override for <strong>{firmName}</strong>.
          The firm's limits will revert to those defined by their subscription plan.
        </p>
      </ConfirmDialog>
    </div>
  );
});

FirmSubscription.displayName = 'FirmSubscription';

export default FirmSubscription;